import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import CreatePackage from "./createpackage/CreatePackage.jsx";
import PackageDetails from "./createpackage/PackageDetails.jsx";
import "./App.css";
import { useState, useEffect, useCallback, createContext, useContext } from 'react';

// Create Auth Context
const AuthContext = createContext();

// Custom Auth Provider
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const AUTH_STORAGE_KEYS = {
    TOKEN: 'authToken',
    OPERATOR_DATA: 'operatorData',
  };

  const checkAuth = useCallback(() => {
    try {
      const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
      const operatorData = localStorage.getItem(AUTH_STORAGE_KEYS.OPERATOR_DATA);
      
      console.log('🔐 Checking auth:', { token: !!token, operatorData: !!operatorData });
      
      const authenticated = !!(token && operatorData);
      setIsAuthenticated(authenticated);
      setIsLoading(false);
      
      return authenticated;
    } catch (error) {
      console.error('❌ Auth check error:', error);
      setIsAuthenticated(false);
      setIsLoading(false);
      return false;
    }
  }, []);

  useEffect(() => {
    console.log('🔄 AuthProvider mounted, checking auth...');
    checkAuth();
    
    // Listen for storage changes (for logout from other tabs)
    const handleStorageChange = (e) => {
      if (e.key === AUTH_STORAGE_KEYS.TOKEN || e.key === null) {
        console.log('📦 Storage changed, rechecking auth...');
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkAuth]);

  const login = useCallback((token, operatorData) => {
    console.log('🔑 Logging in user...');
    try {
      localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
      if (operatorData) {
        localStorage.setItem(AUTH_STORAGE_KEYS.OPERATOR_DATA, JSON.stringify(operatorData));
      }
      setIsAuthenticated(true);
      console.log('✅ Login successful');
    } catch (error) {
      console.error('❌ Login error:', error);
    }
  }, []);

  const logout = useCallback(() => {
    console.log('🚪 Logging out user...');
    try {
      localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN);
      localStorage.removeItem(AUTH_STORAGE_KEYS.OPERATOR_DATA);
      setIsAuthenticated(false);
      console.log('✅ Logout successful');
    } catch (error) {
      console.error('❌ Logout error:', error);
    }
  }, []);

  const getToken = useCallback(() => {
    return localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
  }, []);

  const getOperatorData = useCallback(() => {
    try {
      const data = localStorage.getItem(AUTH_STORAGE_KEYS.OPERATOR_DATA);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('❌ Error parsing operator data:', error);
      return null;
    }
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    login,
    logout,
    getToken,
    getOperatorData,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function App() {
  const { isAuthenticated, isLoading } = useAuth();

  console.log("🎯 App auth state:", { isAuthenticated, isLoading });

  if (isLoading) {
    return (
      <div className="App">
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        {/* Public routes - redirect to home if authenticated */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
        />
        <Route
          path="/signup"
          element={isAuthenticated ? <Navigate to="/" replace /> : <Signup />}
        />

        {/* Protected routes - redirect to login if not authenticated */}
        <Route
          path="/"
          element={isAuthenticated ? <Home /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/home"
          element={isAuthenticated ? <Home /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/create-package"
          element={isAuthenticated ? <CreatePackage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/package-details"
          element={isAuthenticated ? <PackageDetails /> : <Navigate to="/login" replace />}
        />

        {/* Catch all route */}
        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />}
        />
      </Routes>
    </div>
  );
}

// Wrap App with Router and AuthProvider
function AppWrapper() {
  return (
    <Router>
      <AuthProvider>
        <App />
      </AuthProvider>
    </Router>
  );
}

export default AppWrapper;